import styled, { css } from 'styled-components';

import { SPACING } from '~/common/layout-propertis/spacing';
import { RADIUS } from '~/common/layout-propertis/radius';

import { TBorderRadius, TDropdownSize, TDropdownVariant } from './types';
import { getParagraphLetterSpacing } from '../../Typography/Paragraph/styles';
import { getParagraphFontSize } from '../../Typography/Paragraph/styles';
import { getParagraphLineHeight } from '../../Typography/Paragraph/styles';

interface IInputWrapperProps {
  $hasError: boolean;
  $size: TDropdownSize;
  $isDisabled: boolean;
  $width: string;
  $variant: TDropdownVariant;
  $borderRadius: TBorderRadius;
}

interface IRightIconWrapperProps {
  $isDropdownVisible: boolean;
}

interface ICustomInputProps {
  $variant: TDropdownVariant;
}

export const Container = styled.label`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${SPACING._4};
`;

export const getDropdownHeight: Record<TDropdownSize, string> = {
  'x-small': '32px',
  small: '36px',
  medium: '40px',
};

export const getDropdownPadding: Record<TDropdownSize, string> = {
  'x-small': `${SPACING._6}`,
  small: `${SPACING._8}`,
  medium: `${SPACING._10}`,
};

const getDropdownGap: Record<TDropdownSize, string> = {
  'x-small': `${SPACING._6}`,
  small: `${SPACING._8}`,
  medium: `${SPACING._8}`,
};

export const InputWrapper = styled.div<IInputWrapperProps>`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  width: ${({ $width }) => $width};
  height: ${({ $size }) => getDropdownHeight[$size]};
  border-radius: ${({ $borderRadius }) =>
    $borderRadius === 'default' ? `${RADIUS._4}` : `${RADIUS._999}`};
  border: ${({ theme }) => `1px solid ${theme.neutral[700]}`};
  background-color: ${({ theme, $borderRadius }) =>
    $borderRadius === 'full' ? '#23262F' : theme.neutral[950]};
  padding: ${({ $size }) => getDropdownPadding[$size]};
  gap: ${({ $size, $variant }) =>
    $variant === 'compact' ? `${SPACING._4}` : getDropdownGap[$size]};
  cursor: pointer;

  transition: all ease-in 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.neutral[900]};
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.neutral[0]};
  }

  ${({ $hasError }) =>
    $hasError &&
    css`
      border-color: ${({ theme }) => theme.red[600]} !important;
    `};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      border-color: ${({ theme }) => theme.neutral[900]};
      background-color: ${({ theme }) => theme.neutral[900]};
      cursor: not-allowed;
    `};
`;

export const CustomInput = styled.input<ICustomInputProps>`
  background: none;
  height: 100%;
  overflow: hidden;
  outline: none;
  border: none;
  flex: 1;
  cursor: pointer;
  width: 100%;
  line-height: ${getParagraphLineHeight['small']}px;
  font-size: ${getParagraphFontSize['small']}px;
  letter-spacing: ${getParagraphLetterSpacing('small')}px;
  font-weight: 400;
  color: ${({ theme }) => theme.neutral[0]};

  &::placeholder {
    color: ${({ theme }) => theme.neutral[400]};
  }

  &:disabled {
    cursor: not-allowed;

    &::placeholder {
      color: ${({ theme }) => theme.neutral[600]};
    }
  }

  ${({ $variant }) =>
    $variant === 'compact' &&
    css`
      max-width: calc(100% - 20px);
    `}
`;

export const RightIconWrapper = styled.div<IRightIconWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;

  transition: all ease-out 0.2s;

  transform: rotate(
    ${({ $isDropdownVisible }) => ($isDropdownVisible ? '180deg' : '0deg')}
  );

  min-width: 20px;
`;

export const StyIconLabelContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${SPACING._8};
`;

export const CloseIcon = styled.button`
  svg {
    transition: 200ms;
  }

  &:hover {
    svg {
      filter: brightness(1.5);
    }
  }
`;
